// anirpack.ts — build the SIM's anim metadata (AnirJson) from a mounted pack's
// RAW .anir files (the same big-endian layout ClientAnimationData parses;
// sim/tools/extract-anir.mjs is the offline twin that produced the committed
// sim/data/anir.json from the 832 pack).
//
// WHY THIS EXISTS (the F3 art-mode finding): the C++ client always feeds its
// sim from the MOUNTED pack's .anir bytes. For 824 that converges with 832 —
// strip names/order, frame counts, delays, loop flags and holds are byte-equal
// across the two packs, and the only differing fields (bCustomData1/2, the
// fire-point pixel offsets) are normalized back to the 832 values by
// AnimationData::GetSpecialPoint32 (Animation.cpp:152-163, round(v*4/3) at
// gcxTile 24; verified exhaustively: 0 violations over all 1829 frames). So
// the committed anir.json (832-derived, gcxTile 32) is exactly right for BOTH
// 8bpp packs. The 2432 pack does NOT converge: its strip lists and frame
// counts/holds genuinely differ (e.g. reactor 'spin 0' 10 frames vs 832's 3;
// proc's icon/smoke strips swap indices; the trailing 'highlight' strips are
// absent), so a C++ ?2432 client runs measurably different anim-driven sim
// timing — a faithful TS ?2432 sim must consume the 2432 pack's own metadata.
// This helper is that root fix: parse the mounted pack, hand the result to
// Sim.load as `anir`.
//
// Layout (game/ht.h:1622-1700, all multi-byte fields big-endian):
//   AnimationFileHeader { u32 cstpd; u32 aoffStpd[cstpd] }   (file offsets)
//   StripData { char szName[26]; u8 cDelay; u8 bfFlags(kfAniLoop=1); u16 cfrmd;
//               FrameData[cfrmd] }
//   FrameData (139 bytes) { char szName[64]; char szName2[64]; u16 ibm; u16 ibm2;
//     u8 cHold; s8 xOrigin; s8 yOrigin; s8 xOrigin2; s8 yOrigin2;
//     u8 bCustomData1; u8 bCustomData2 }  (custom data sign-extends on read)

import type { AnirJson } from '../../../sim/src/index.ts';
import type { PackFileReader } from '../io/packfile.ts';

const KCB_FRAMEDATA = 139;
const KCB_STRIPDATA_HEADER = 30; // szName[26] + cDelay + bfFlags + cfrmd

const s8 = (v: number): number => (v << 24) >> 24;

function cstr(b: Uint8Array, off: number, max: number): string {
  let s = '';
  for (let i = 0; i < max && b[off + i] !== 0; i++) s += String.fromCharCode(b[off + i]);
  return s;
}

interface AnirStrip {
  name: string;
  delay: number;
  loop: boolean;
  frames: { hold: number; s1: number; s2: number }[];
}

/** Parse one raw .anir file into the sim's strip metadata (the extract-anir.mjs
 *  field subset: makeAnimRegistry consumes name/delay/loop + hold/s1/s2). */
export function parseAnir(b: Uint8Array): AnirStrip[] {
  const cstpd = ((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0;
  const strips: AnirStrip[] = [];
  for (let i = 0; i < cstpd; i++) {
    const p = 4 + i * 4;
    const so = ((b[p] << 24) | (b[p + 1] << 16) | (b[p + 2] << 8) | b[p + 3]) >>> 0;
    const cfrmd = (b[so + 28] << 8) | b[so + 29];
    const frames: AnirStrip['frames'] = [];
    for (let f = 0, fo = so + KCB_STRIPDATA_HEADER; f < cfrmd; f++, fo += KCB_FRAMEDATA) {
      frames.push({ hold: b[fo + 132], s1: s8(b[fo + 137]), s2: s8(b[fo + 138]) });
    }
    strips.push({
      name: cstr(b, so, 26),
      delay: b[so + 26],
      loop: (b[so + 27] & 1) !== 0,
      frames,
    });
  }
  return strips;
}

/** Build the Sim.load `anir` input from every .anir in the mounted pack
 *  (keys are bare lowercase names, like sim/data/anir.json's). */
export function anirJsonFromPack(pakr: PackFileReader): AnirJson {
  const out: Record<string, AnirStrip[]> = {};
  for (const fn of pakr.EnumFiles('first')) {
    if (!fn.toLowerCase().endsWith('.anir')) continue;
    const bytes = pakr.MapFile(fn);
    if (bytes === null) continue;
    out[fn.toLowerCase().replace(/\.anir$/, '')] = parseAnir(bytes);
  }
  return out as unknown as AnirJson;
}
